// Slice 2 — Barber ↔ Xero employee mapping section.
// Renders inside the Payroll Settings page below the connection card.
//
// Behaviour:
//   - If no tenants connected: render nothing.
//   - If multiple tenants: tenant selector at top.
//   - Fetches Xero employees (read-only) + existing mappings, joins by barber.
//   - On a 403 from /xero/payroll/employees we show a "Re-consent required"
//     prompt — happens when the existing token predates the new
//     payroll.employees.read scope.

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const inputStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-background)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  width: '100%',
};

const buttonSmall: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-2)',
  borderRadius: 'var(--radius-default)',
  border: 'none',
  backgroundColor: 'var(--color-accent)',
  color: 'var(--color-surface)',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
};

const buttonGhost: React.CSSProperties = {
  ...buttonSmall,
  backgroundColor: 'transparent',
  color: 'var(--color-text-muted)',
  border: '1px solid var(--color-border)',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '2px solid var(--color-border)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 'var(--font-size-sm)',
  verticalAlign: 'middle',
};

interface Props {
  tenants: { tenantId: string; tenantName: string }[];
  onReconsent: () => void;
}

export function PayrollMapping({ tenants, onReconsent }: Props) {
  const queryClient = useQueryClient();
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [draftSelections, setDraftSelections] = useState<Record<number, string>>({});
  const [savingBarber, setSavingBarber] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [clearTarget, setClearTarget] = useState<{ barberId: number; barberName: string } | null>(
    null,
  );

  // Default to first tenant when the list arrives.
  useEffect(() => {
    if (!selectedTenantId && tenants.length > 0) {
      setSelectedTenantId(tenants[0].tenantId);
    }
  }, [tenants, selectedTenantId]);

  const employeesQuery = useQuery({
    queryKey: ['xero-employees', selectedTenantId],
    queryFn: () => api.xeroListEmployees(selectedTenantId),
    enabled: Boolean(selectedTenantId),
    retry: false,
  });

  const mappingsQuery = useQuery({
    queryKey: ['barber-mappings', selectedTenantId],
    queryFn: () => api.payrollBarberMappings(selectedTenantId),
    enabled: Boolean(selectedTenantId),
  });

  const employees = employeesQuery.data?.employees ?? [];
  const mappings = mappingsQuery.data?.mappings ?? [];

  // Backend returns a structured status field — read directly instead of
  // re-parsing thrown error messages.
  const errorKind = useMemo<'reconsent' | 'no-payroll' | 'other' | null>(() => {
    if (employeesQuery.error) return 'other';
    const status = employeesQuery.data?.status;
    if (status === 'no-payroll') return 'no-payroll';
    if (status === 'reconsent') return 'reconsent';
    if (status === 'error') return 'other';
    return null;
  }, [employeesQuery.data, employeesQuery.error]);

  // Lookup map for resolving terminated employees still mapped against
  // a barber. Must be declared before any early return — Rules of Hooks.
  const employeeNamesById = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of employees) m.set(e.employeeId, `${e.firstName} ${e.lastName}`.trim());
    return m;
  }, [employees]);

  if (tenants.length === 0) return null;

  async function handleSave(barberId: number) {
    const employeeId = draftSelections[barberId];
    if (!employeeId || !selectedTenantId) return;
    const employee = employees.find((e) => e.employeeId === employeeId);
    if (!employee) return;
    setSavingBarber(barberId);
    setErrorMsg(null);
    try {
      await api.payrollUpsertBarberMapping({
        barberId,
        tenantId: selectedTenantId,
        xeroEmployeeId: employee.employeeId,
        xeroEmployeeName: `${employee.firstName} ${employee.lastName}`.trim(),
      });
      queryClient.invalidateQueries({ queryKey: ['barber-mappings', selectedTenantId] });
      // Clear the draft for that barber.
      setDraftSelections((s) => {
        const next = { ...s };
        delete next[barberId];
        return next;
      });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSavingBarber(null);
    }
  }

  async function confirmClear() {
    if (!clearTarget || !selectedTenantId) return;
    const { barberId } = clearTarget;
    setClearTarget(null);
    setSavingBarber(barberId);
    try {
      await api.payrollDeleteBarberMapping(barberId, selectedTenantId);
      queryClient.invalidateQueries({ queryKey: ['barber-mappings', selectedTenantId] });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Remove failed');
    } finally {
      setSavingBarber(null);
    }
  }

  const mappedCount = mappings.filter((m) => m.xeroEmployeeId).length;

  return (
    <div style={cardStyle}>
      <h2 style={{ marginTop: 0 }}>Barber → Xero employee mapping</h2>
      <p
        style={{ color: 'var(--color-text-muted)', marginTop: 0, fontSize: 'var(--font-size-sm)' }}
      >
        <strong>Slice 2</strong> — link each barber on this booking system to the matching Xero
        employee record. Required before we can push timesheets or pay runs. Read-only call to Xero
        — no employee data is written back.
      </p>

      {tenants.length > 1 && (
        <div style={{ marginBottom: 'var(--spacing-3)' }}>
          <label
            style={{
              display: 'block',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--color-text-muted)',
              marginBottom: 'var(--spacing-1)',
            }}
          >
            Xero organisation
          </label>
          <select
            style={inputStyle}
            value={selectedTenantId}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setSelectedTenantId(value);
            }}
          >
            {tenants.map((t) => (
              <option key={t.tenantId} value={t.tenantId}>
                {t.tenantName}
              </option>
            ))}
          </select>
        </div>
      )}

      {errorKind === 'reconsent' && (
        <div
          style={{
            padding: 'var(--spacing-3)',
            backgroundColor: 'rgba(237, 137, 54, 0.12)',
            border: '1px solid #ed8936',
            borderRadius: 'var(--radius-default)',
            marginBottom: 'var(--spacing-3)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          <strong>Re-consent required.</strong> Your existing Xero connection doesn't include the{' '}
          <code>payroll.employees.read</code> scope. Click Re-consent to grant the new permission.
          <div style={{ marginTop: 'var(--spacing-2)' }}>
            <button style={buttonSmall} onClick={onReconsent}>
              Re-consent
            </button>
          </div>
        </div>
      )}

      {errorKind === 'no-payroll' && (
        <div
          style={{
            padding: 'var(--spacing-3)',
            backgroundColor: 'rgba(237, 137, 54, 0.12)',
            border: '1px solid #ed8936',
            borderRadius: 'var(--radius-default)',
            marginBottom: 'var(--spacing-3)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          <strong>Xero Payroll is not provisioned</strong> on this Xero organisation. The connection
          worked, but Payroll is a separate Xero module that has to be set up before we can list
          employees, push timesheets, or run pay runs.
          <ul style={{ marginTop: 'var(--spacing-2)', marginBottom: 0, paddingLeft: 20 }}>
            <li>
              In Xero: <strong>Settings → Payroll Settings</strong> to set it up (subscription with
              Payroll required).
            </li>
            <li>
              Or disconnect and reconnect to <strong>Demo Company (AU)</strong> for testing.
            </li>
          </ul>
        </div>
      )}

      {errorKind === 'other' && (
        <div
          style={{
            padding: 'var(--spacing-2) var(--spacing-3)',
            backgroundColor: 'rgba(229, 62, 62, 0.1)',
            border: '1px solid var(--color-danger, #e53e3e)',
            borderRadius: 'var(--radius-default)',
            color: 'var(--color-danger, #e53e3e)',
            marginBottom: 'var(--spacing-3)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          Couldn't load Xero employees:{' '}
          {employeesQuery.data?.error ??
            (employeesQuery.error instanceof Error
              ? employeesQuery.error.message
              : 'unknown error')}
        </div>
      )}

      {errorMsg && (
        <div
          style={{
            padding: 'var(--spacing-2) var(--spacing-3)',
            backgroundColor: 'rgba(229, 62, 62, 0.1)',
            border: '1px solid var(--color-danger, #e53e3e)',
            borderRadius: 'var(--radius-default)',
            color: 'var(--color-danger, #e53e3e)',
            marginBottom: 'var(--spacing-3)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {errorMsg}
        </div>
      )}

      {employeesQuery.isLoading || mappingsQuery.isLoading ? (
        <div>Loading employees and mappings…</div>
      ) : errorKind === 'reconsent' || errorKind === 'no-payroll' ? null : (
        <>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            {mappedCount} of {mappings.length} barbers mapped · {employees.length} active Xero
            employees available
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Barber</th>
                <th style={thStyle}>Xero employee</th>
                <th style={thStyle} />
              </tr>
            </thead>
            <tbody>
              {mappings.map((m) => {
                const draft = draftSelections[m.barberId];
                const currentChoice = draft ?? m.xeroEmployeeId ?? '';
                const isDirty =
                  draft !== undefined && draft !== '' && draft !== (m.xeroEmployeeId ?? '');
                return (
                  <tr key={m.barberId}>
                    <td style={tdStyle}>
                      <strong>{m.barberName}</strong>
                      {!m.xeroEmployeeId && (
                        <div
                          style={{
                            fontSize: 'var(--font-size-xs)',
                            color: '#ed8936',
                            marginTop: 2,
                          }}
                        >
                          Not yet mapped
                        </div>
                      )}
                      {m.xeroEmployeeId && m.mappedByEmail && (
                        <div
                          style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-muted)',
                            marginTop: 2,
                          }}
                        >
                          mapped by {m.mappedByEmail}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <select
                        style={inputStyle}
                        value={currentChoice}
                        onChange={(e) => {
                          const value = e.currentTarget.value;
                          setDraftSelections((s) => ({ ...s, [m.barberId]: value }));
                        }}
                        disabled={savingBarber === m.barberId}
                      >
                        <option value="">— Select Xero employee —</option>
                        {employees.map((e) => (
                          <option key={e.employeeId} value={e.employeeId}>
                            {e.firstName} {e.lastName}
                            {e.email ? ` · ${e.email}` : ''}
                          </option>
                        ))}
                        {/* If the saved mapping references an employee that's no longer in the
                            active list (e.g. terminated), surface it so the row still renders. */}
                        {m.xeroEmployeeId && !employeeNamesById.has(m.xeroEmployeeId) && (
                          <option value={m.xeroEmployeeId}>
                            {m.xeroEmployeeName} (no longer active)
                          </option>
                        )}
                      </select>
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      {isDirty && (
                        <button
                          style={buttonSmall}
                          onClick={() => handleSave(m.barberId)}
                          disabled={savingBarber === m.barberId}
                        >
                          {savingBarber === m.barberId ? 'Saving…' : 'Save'}
                        </button>
                      )}
                      {!isDirty && m.xeroEmployeeId && (
                        <button
                          style={buttonGhost}
                          onClick={() =>
                            setClearTarget({ barberId: m.barberId, barberName: m.barberName })
                          }
                          disabled={savingBarber === m.barberId}
                        >
                          Clear
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      <ConfirmDialog
        isOpen={Boolean(clearTarget)}
        title={`Remove mapping for ${clearTarget?.barberName ?? ''}?`}
        message="Future timesheets will no longer link to a Xero employee for this barber. You can re-map any time."
        confirmLabel="Remove"
        tone="danger"
        onConfirm={confirmClear}
        onCancel={() => setClearTarget(null)}
      />
    </div>
  );
}
